import { Box, Card, Divider } from '@mui/material';
import { Fragment } from 'react';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useVariableSourceList } from '../hooks/useVariableSourceList';
import type { VariableSourceListProps } from '../types';
import { VariablesTitle } from './AnalyticalVariablesControls';
import { SortableVariableItem } from './SortableVariableItem';
import { SortableVariableList } from './SortableVariableList';
import { VariableSourceListHeader } from './VariableSourceListHeader';
import { VariableSourceRow } from './VariableSourceRow';
import { VariableSourceRowSkeleton } from './VariableSourceRowSkeleton';

export const VariableSourceList = ({ datasetSources, title }: VariableSourceListProps) => {
  const {
    rows,
    isLoading,
    previewAvId,
    onClosePreview,
    renderVariablePreview,
    isEditDisabled,
    deleteTooltip,
    onReorder,
  } = useVariableSourceList(datasetSources);

  // Rows swap for skeletons while the variable lookup loads, so dragging waits
  // for it; it is also off while editing is locked.
  const isReorderEnabled = !isEditDisabled && !isLoading;
  // The default rows are pinned first and have no source to move.
  const draggableKeys = rows.filter((row) => row.status !== 'default').map((row) => row.key);

  const renderRows = () =>
    rows.map((row, index) => (
      <Fragment key={row.key}>
        {index > 0 && <Divider />}
        {isLoading && row.avId !== null ? (
          <VariableSourceRowSkeleton />
        ) : isReorderEnabled && row.status !== 'default' ? (
          <SortableVariableItem id={row.key}>
            {(dragHandleProps) => (
              <VariableSourceRow
                row={row}
                isEditDisabled={isEditDisabled}
                deleteTooltip={deleteTooltip}
                dragHandleProps={dragHandleProps}
              />
            )}
          </SortableVariableItem>
        ) : (
          <VariableSourceRow
            row={row}
            isEditDisabled={isEditDisabled}
            deleteTooltip={deleteTooltip}
          />
        )}
      </Fragment>
    ));

  return (
    <>
      <Card
        variant="outlined"
        data-testid="analysis-variable-list"
        sx={{ borderRadius: `${borderRadius.md}px`, overflow: 'hidden' }}
      >
        {/* Where the grid's toolbar carries the title in the Table view. */}
        <Box sx={{ px: 4, pt: 3, pb: 1 }} data-testid="analysis-variable-list-title">
          <VariablesTitle title={title} />
        </Box>
        <VariableSourceListHeader />
        <Divider />
        {isReorderEnabled ? (
          <SortableVariableList itemIds={draggableKeys} onReorder={onReorder}>
            {renderRows()}
          </SortableVariableList>
        ) : (
          renderRows()
        )}
      </Card>
      {renderVariablePreview?.({ avId: previewAvId, onClose: onClosePreview })}
    </>
  );
};
